import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  checkExportingFilesApiV1CasesCaseNumberCheckExportingFilesGet,
  getAllCasesApiV1CasesGet,
  getCaseByCaseNumberEndpointApiV1CasesGetCaseByCaseNumberCaseNumberGet,
  getCaseEndpointApiV1CasesCaseIdGet,
} from "../../api/generated/sdk.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
// Type-only: erased at compile time, so this does not pull callApi's MSAL graph
// into every module that fetches a case.
import type { ApiError } from "../../utils/callApi";
import { buildApiErrorMessage } from "../../utils/notification";

/**
 * Fetch all cases with optional filtering
 * @param case_number - Filter by case number (partial match)
 * @param claim_number - Filter by claim number (partial match)
 * @param examinee_name - Filter by examinee name (partial match)
 * @param search - Search across case_number, claim_number, and examinee_name fields (partial match)
 * @param template_id - Filter by template ID
 * @param created_at - Filter by created at date
 * @param created_at_start - Filter by created at start date
 * @param created_at_end - Filter by created at end date
 * @param appointment_date - Filter by appointment date
 * @param status - Filter by case status
 * @param enabled - Whether the query should run
 * @param view_type - Filter by view type
 * @returns Query result with cases data
 */
export const useCases = ({
  case_number,
  claim_number,
  examinee_name,
  search,
  template_id,
  created_at,
  created_at_start,
  created_at_end,
  appointment_date,
  status,
  enabled = true,
  view_type = "",
}: {
  case_number?: string | null;
  claim_number?: string | null;
  examinee_name?: string | null;
  search?: string | null;
  template_id?: string | null;
  created_at?: string | null;
  created_at_start?: string | null;
  created_at_end?: string | null;
  appointment_date?: string | null;
  status?: string;
  enabled?: boolean;
  view_type?: string;
} = {}) => {
  return useQuery({
    queryKey: [
      CLIENT_KEYS.CASES,
      {
        case_number,
        claim_number,
        examinee_name,
        search,
        template_id,
        created_at,
        created_at_start,
        created_at_end,
        appointment_date,
        status,
        view_type,
      },
    ],
    queryFn: async () => {
      const response = await getAllCasesApiV1CasesGet({
        client: apiClient,
        query: {
          case_number,
          claim_number,
          examinee_name,
          search,
          template_id,
          created_at,
          created_at_start,
          created_at_end,
          appointment_date,
          view_type,
          status,
        },
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to fetch cases", response.error)
        );
      }

      // Normalize to an array: DataTable calls .map() on this unconditionally.
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled,
  });
};

/**
 * Fetch a single case by case number
 * @param case_number - The case number to fetch
 * @param enabled - Whether the query should run
 * @returns Query result with case data
 */
export const useCaseByCaseNumber = ({
  case_number,
  enabled = true,
}: {
  case_number: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.CASES, "by-case-number", case_number],
    queryFn: async () => {
      const response =
        await getCaseByCaseNumberEndpointApiV1CasesGetCaseByCaseNumberCaseNumberGet({
          client: apiClient,
          path: {
            case_number,
          },
        });

      // Check for errors in the response
      if ("error" in response && response.error) {
        const detail =
          typeof response.error.detail === "string"
            ? response.error.detail
            : JSON.stringify(response.error);

        // The status has to survive the throw: the case shell renders a
        // different screen for 403 (someone else's case), 404 (gone, or not
        // released to this doctor) and 5xx (retryable) — see
        // constants/case-access.ts. A bare `Error` collapsed all three into one
        // indistinguishable failure. `detail` rides along for debugging; it is
        // NOT for display (architecture/error-handling.md).
        const error = new Error(
          buildApiErrorMessage("Failed to fetch case", response.error)
        ) as ApiError;
        error.status = response.response?.status;
        error.detail = detail;
        throw error;
      }

      return response.data;
    },
    enabled: enabled && !!case_number,
    // A 403/404 answers the same way however many times it's asked, so the
    // default single retry only doubled the time the viewer stared at a
    // half-painted case before the notice could appear. 408/429 are excluded:
    // those ARE transient behind Front Door and deserve the retry.
    retry: (failureCount, error) => {
      const { status } = error as ApiError;
      const isTransient = status === 408 || status === 429;
      if (status && status >= 400 && status < 500 && !isTransient) return false;
      return failureCount < 1;
    },
  });
};

/**
 * Fetch a single case by case ID
 * @param case_id - The case ID to fetch
 * @param enabled - Whether the query should run
 * @returns Query result with case data
 */
export const useCaseById = ({
  case_id,
  enabled = true,
}: {
  case_id: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.CASES, case_id],
    queryFn: async () => {
      const response = await getCaseEndpointApiV1CasesCaseIdGet({
        client: apiClient,
        path: {
          case_id,
        },
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to fetch case", response.error)
        );
      }

      return response.data;
    },
    enabled: enabled && !!case_id,
  });
};

/**
 * Check for files that are currently being exported for a specific case
 * @param case_number - The case number to check for exporting files
 * @param enabled - Whether the query should run
 * @returns Query result with exporting files data including total_count
 */
export const useCheckExportingFiles = ({
  case_number,
  enabled = true,
}: {
  case_number: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.CASES, case_number, CLIENT_KEYS.CHECK_EXPORTING_FILES],
    queryFn: async () => {
      const response =
        await checkExportingFilesApiV1CasesCaseNumberCheckExportingFilesGet({
          client: apiClient,
          path: {
            case_number,
          },
        });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to check exporting files", response.error)
        );
      }

      return response.data;
    },
    enabled: enabled && !!case_number,
  });
};
